import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClockIcon, TagIcon, ArrowRightIcon, FileTextIcon, DownloadIcon } from 'lucide-react';
import { ServiceNowSubNav } from '../components/ServiceNowSubNav';
import { articles } from '../data/articles';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const posts = articles;
export function ThoughtLeadershipPage() {
  return <div className="w-full" data-id="element-3265" data-mp="1riy4jp1vxhbu">
      <ServiceNowSubNav data-id="element-3266" data-mp="1rjr2c0iv8whu" />
      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-3267" data-mp="1rio8pku5sfy8">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-3268" data-mp="1wgzm25jnx8l7">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-3269" data-mp="g39fms52m88e">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto" data-id="element-3270" data-mp="d0bdox5zarbx">
              Thought Leadership
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-3271" data-mp="831dcmh1wv9b"></div>
            <p className="text-xl text-nrs-light font-bold mb-4 max-w-2xl mx-auto" data-id="element-3272" data-mp="mzil73q17rr4">
              Practical insights on ServiceNow governance, platform stewardship,
              and federal IT strategy from the New River Systems team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED WHITE PAPER */}
      <section className="section-light-blue border-b border-gray-200" data-id="element-3273" data-mp="qyhzhexnl0v8">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-3274" data-mp="15opbbbt5nlmy">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="bg-white rounded-xl border-2 border-nrs-orange overflow-hidden" data-id="element-3275" data-mp="10r6y3983uk95">
            <div className="grid grid-cols-1 lg:grid-cols-3" data-id="element-3276" data-mp="1hsb36h1h6p80">
              <div className="bg-nrs-orange p-8 flex flex-col justify-center" data-id="element-3277" data-mp="1hpoxyo224lsy">
                <FileTextIcon className="h-12 w-12 text-white mb-4" data-id="element-3278" data-mp="1mqw46iojxcxi" />
                <div className="text-white font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-3279" data-mp="1roo685iwufff">
                  Featured Governance Guide
                </div>
                <h2 className="font-heading font-bold text-xl text-white leading-tight" data-id="element-3280" data-mp="20ij20my56tie">
                  The Three Pillars of ServiceNow Governance: A Federal Agency
                  Field Guide
                </h2>
              </div>
              <div className="lg:col-span-2 p-8" data-id="element-3281" data-mp="2ahi9w3b32l4c">
                <div className="flex items-center gap-3 mb-4" data-id="element-3282" data-mp="2ff0czd2f664f">
                  <span className="px-2 py-0.5 bg-nrs-orange/10 text-nrs-orange text-xs font-semibold rounded-full" data-id="element-3283" data-mp="2feihiinkzvxk">
                    Governance Guide
                  </span>
                  <span className="text-gray-400 text-xs" data-id="element-3284" data-mp="l225f183tnks">7 pages</span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-6" data-id="element-3285" data-mp="2ycbzn4l0lr04">
                  A practical guide to the three governance gaps ServiceNow
                  Impact and your SI don't cover: Technical Policy, Data, and
                  Strategic Governance. Includes a Crawl/Walk/Run maturity
                  snapshot for each pillar.
                </p>
                <a href="https://drive.google.com/file/d/1J8g2FBnUX_0SU3OsQdAGsk0fzTrcJbak/view?usp=drive_link" target="_blank" rel="noopener noreferrer" className="btn-primary" data-id="element-3286" data-mp="3ha4udjlhiumf">
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3287" data-mp="389q3m4pr4nbj" />
                  Download Governance Guide
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOG POSTS */}
      <section className="section-white" data-id="element-3288" data-mp="1wutbun26nk0h">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-3289" data-mp="1wvum4pbdk2h5">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-3290" data-mp="1ruouyvxd4n9q">
            Latest Articles
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-3291" data-mp="3fqlalw4jarco"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-3292" data-mp="3kjwj01ozvg6p">
            {posts.map((post, i) => <motion.article key={post.slug} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full p-0 overflow-hidden" data-id="element-3293" data-mp="36s9ddb95wvb9" data-mpi={i}>
                <div className="bg-nrs-light h-3" data-id="element-3294" data-mp="3c2gbnl95wbg" />
                <div className="p-6 flex flex-col flex-grow" data-id="element-3295" data-mp="7c2xvh3nenmq">
                  <div className="flex items-center gap-3 mb-3" data-id="element-3296" data-mp="7bjo4no5j5js">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${post.tagColor}`} data-id="element-3297" data-mp="3pgwfplzyf6l5">
                      {post.tag}
                    </span>
                    <div className="flex items-center gap-1 text-gray-400 text-xs" data-id="element-3298" data-mp="2e2spmqwhm2ld">
                      <ClockIcon className="h-3 w-3" data-id="element-3299" data-mp="2e55bvg9a2z48" />
                      {post.readTime}
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-nrs-navy mb-3 leading-snug" data-id="element-3300" data-mp="2bhl6q3nql5ht">
                    {post.cardTitle}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5 flex-grow" data-id="element-3301" data-mp="26lma5ss98h6b">
                    {post.excerpt}
                  </p>
                  <Link to={`/services/servicenow/thought-leadership/${post.slug}`} className="inline-flex items-center gap-2 text-nrs-blue font-semibold text-sm hover:text-nrs-orange transition-colors mt-auto" data-id="element-3302" data-mp="2fl126wo2kzsx">
                    Read Article
                    <ArrowRightIcon className="h-4 w-4" data-id="element-3303" data-mp="1nqpa8t8c5m48" />
                  </Link>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="section-navy" data-id="element-3304" data-mp="1wrnbj3re3fj4">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-3305" data-mp="19348b2n75sxw">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-3306" data-mp="145cg07in44t6">
            ServiceNow Governance Insights
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-8" data-id="element-3307" data-mp="3460w87r34sfb"></div>
          <p className="text-xl text-nrs-light mb-8 max-w-2xl mx-auto" data-id="element-3308" data-mp="395lwqmlwl8u5">
            Monthly newsletter with governance frameworks, case studies, and
            federal IT strategy. No spam.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-id="element-3309" data-mp="3iw84jundov2a">
            <input type="email" placeholder="your.email@agency.gov" className="flex-1 px-4 py-3 rounded-md text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-nrs-orange" data-id="element-3310" data-mp="2l8g32ohaf7ow" />
            <button className="btn-primary" data-id="element-3311" data-mp="2v6dqj9rbp0gg">Subscribe</button>
          </div>
        </div>
      </section>
    </div>;
}